import React, { useRef, useState, useEffect, useCallback } from 'react';
import { devLog, devWarn, devError } from '../utils/devLogger';
import styled from 'styled-components';
import { FiCamera, FiPlus, FiTrash2 } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { uploadCommonFile } from '../lib/api/common/commonApi';
import { useLang } from '../context/LangContext';

interface ProfileImageAddProps {
  /** 초기 프로필 이미지 파일명 (서버에서 받은 파일명) */
  initialImageFileName?: string;
  /** 이미지 크기 (기본값: 120px) */
  size?: number;
  /** 이미지 업로드 성공 시 호출되는 콜백 (파일명 전달) */
  onImageUploaded?: (fileName: string) => void;
  /** 이미지가 삭제되었을 때 호출되는 콜백 */
  onImageDelete?: () => void;
  /** 비활성화 상태 */
  disabled?: boolean;
}

const getProfileImageSrc = (profileImageUrl?: string | null) => {
  if (!profileImageUrl || profileImageUrl.trim() === "") {
    return "/fluent_person.svg";
  }
  
  // 이미 완전한 URL인 경우 (http, https로 시작하거나 blob: URL)
  if (profileImageUrl.startsWith("http") || profileImageUrl.startsWith("blob:")) {
    return profileImageUrl;
  }
  
  // 절대 경로인 경우 (/로 시작)
  if (profileImageUrl.startsWith("/")) {
    return `${process.env.REACT_APP_API_HOST}${profileImageUrl}`;
  }
  
  // 단순 파일명인 경우 (서버에서 받은 파일명)
  return `${process.env.REACT_APP_API_HOST}/${profileImageUrl}`;
};

const ProfileImageAdd: React.FC<ProfileImageAddProps> = ({
  initialImageFileName,
  size = 120,
  onImageUploaded,
  onImageDelete,
  disabled = false,
}) => {
  const { lang } = useLang();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [imgError, setImgError] = useState(false);
  const [localPreview, setLocalPreview] = useState<string | null>(null);
  const [currentFileName, setCurrentFileName] = useState<string | undefined>(initialImageFileName);

  // initialImageFileName이 변경될 때 currentFileName 업데이트
  useEffect(() => {
    setCurrentFileName(initialImageFileName);
  }, [initialImageFileName]);

  // 현재 표시할 이미지 URL 결정
  const getCurrentImageUrl = useCallback(() => {
    // 1. 로컬 미리보기가 있으면 우선 표시
    if (localPreview) {
      return localPreview;
    }
    
    // 2. 서버 파일명이 있으면 API 호스트와 결합
    if (currentFileName) {
      return getProfileImageSrc(currentFileName);
    }
    
    // 3. 둘 다 없으면 기본 이미지
    return "/fluent_person.svg";
  }, [localPreview, currentFileName]);

  // 이미지 상태가 변경될 때 에러 상태 초기화
  useEffect(() => {
    const imageUrl = getCurrentImageUrl();
    if (imageUrl && imageUrl !== "/fluent_person.svg") {
      setImgError(false);
    }
  }, [localPreview, currentFileName, getCurrentImageUrl]);

  const handleImageClick = () => {
    if (disabled) return;
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // 파일 검증
      if (file.size > 5 * 1024 * 1024) {
        const fileSizeErrorMessage = lang === 'ko' 
          ? "파일 크기가 5MB를 초과할 수 없습니다." 
          : "File size cannot exceed 5MB.";
        toast.error(fileSizeErrorMessage);
        return;
      }
      
      if (!file.type.startsWith('image/')) {
        const imageOnlyMessage = lang === 'ko' 
          ? "이미지 파일만 업로드할 수 있습니다." 
          : "Only image files can be uploaded.";
        toast.error(imageOnlyMessage);
        return;
      }

      // 로컬 미리보기 즉시 설정
      const reader = new FileReader();
      reader.onload = (e) => {
        const result = e.target?.result as string;
        setLocalPreview(result);
      };
      reader.readAsDataURL(file);

      try {
        // 파일을 FormData로 감싸서 서버에 업로드
        const uploadFormData = new FormData();
        uploadFormData.append('file', file);
        
        // uploadCommonFile 호출
        const uploadRes = await uploadCommonFile(uploadFormData, true);
        devLog('📤 프로필 이미지 업로드 응답:', uploadRes);
        
        const res = Array.isArray(uploadRes) ? uploadRes[0] : uploadRes;
        if (res?.message === "success") {
          const uploadedData = res?.data ?? [];
          
          if (uploadedData.length > 0) {
            // 첫 번째 파일의 정보 사용
            const fileInfo = uploadedData[0];
            const fileName = typeof fileInfo === 'string' ? fileInfo : fileInfo?.fileName || fileInfo?.index;
            
            devLog('업로드된 파일 정보:', fileInfo);
            devLog('사용할 파일명/index:', fileName);
            
            if (fileName !== undefined && fileName !== null) {
              // 업로드 성공 시 서버 파일명으로 업데이트
              setCurrentFileName(String(fileName));
              devLog('✅ 프로필 이미지 업로드 성공, 파일명:', fileName);
              
              // 부모 컴포넌트에 성공 콜백 호출
              if (onImageUploaded) {
                onImageUploaded(String(fileName));
              }
            } else {
              devError('❌ 업로드된 파일의 정보를 찾을 수 없습니다:', uploadedData);
              const fileInfoErrorMessage = lang === 'ko' 
                ? "업로드된 파일 정보를 확인할 수 없습니다." 
                : "Unable to verify uploaded file information.";
              toast.error(fileInfoErrorMessage);
              // 업로드 실패 시 로컬 미리보기 제거
              setLocalPreview(null);
            }
          } else {
            const noFileInfoMessage = lang === 'ko' 
              ? "업로드된 파일 정보가 없습니다." 
              : "No uploaded file information.";
            toast.error(noFileInfoMessage);
            // 업로드 실패 시 로컬 미리보기 제거
            setLocalPreview(null);
          }
        } else {
          const errorMessage = res?.error?.message || (lang === 'ko' 
            ? "프로필 이미지 업로드에 실패했습니다." 
            : "Profile image upload failed.");
          devError('업로드 실패:', errorMessage);
          toast.error(errorMessage);
          // 업로드 실패 시 로컬 미리보기 제거
          setLocalPreview(null);
        }
      } catch (error) {
        devError('이미지 업로드 실패:', error);
        toast.error("프로필 이미지 업로드에 실패했습니다.");
        // 업로드 실패 시 로컬 미리보기 제거
        setLocalPreview(null);
      }
    }
    // 같은 파일을 다시 선택할 수 있도록 input value 리셋
    event.target.value = '';
  };

  const handleDeleteClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    setLocalPreview(null);
    setCurrentFileName(undefined);
    if (onImageDelete) {
      onImageDelete();
    }
  };

  const handleImageError = () => {
    const currentImageUrl = getCurrentImageUrl();
    devLog("프로필 이미지 로드 실패, 기본 이미지로 대체");
    devLog("원본 imageUrl:", currentImageUrl);
    devLog("변환된 전체 경로:", currentImageUrl);
    devLog("API_HOST 환경변수:", process.env.REACT_APP_API_HOST);
    setImgError(true);
  };

  const currentImageUrl = getCurrentImageUrl();
  const isDefaultImage = currentImageUrl === "/fluent_person.svg";
  const hasValidImage = !isDefaultImage && !imgError;

  return (
    <Container size={size}>
      <ImageContainer
        onClick={handleImageClick}
        hasImage={hasValidImage}
        disabled={disabled}
        size={size}
      >
        {hasValidImage ? (
          <ProfileImage 
            src={currentImageUrl} 
            alt="Profile" 
            size={size}
            onError={handleImageError}
          />
        ) : isDefaultImage ? (
          <ProfileImage 
            src="/fluent_person.svg" 
            alt="Default Profile" 
            size={size}
            $isError={true}
          />
        ) : (
          <EmptyImageContainer size={size}>
            <PlusIconWrapper size={size * 0.3}>
              {FiPlus({})}
            </PlusIconWrapper>
          </EmptyImageContainer>
        )}
      </ImageContainer>
      
      <CameraIconWrapper size={size * 0.2}>
        {FiCamera({})}
      </CameraIconWrapper>
      
      {!isDefaultImage && (
        <DeleteIconWrapper
          onClick={handleDeleteClick}
          size={size * 0.18}
        >
          {FiTrash2({})}
        </DeleteIconWrapper>
      )}
      
      <HiddenInput
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileChange}
      />
    </Container>
  );
};

const Container = styled.div<{ size: number }>`
  position: relative;
  width: ${props => props.size}px;
  height: ${props => props.size}px;
`;

const ImageContainer = styled.div<{ 
  hasImage: boolean; 
  disabled: boolean; 
  size: number; 
}>`
  position: relative;
  width: 100%;
  height: 100%;
  cursor: ${props => props.disabled ? 'default' : 'pointer'};
  opacity: ${props => props.disabled ? 0.5 : 1};
  border-radius: ${props => props.hasImage ? '50%' : '8px'};
  overflow: ${props => props.hasImage ? 'visible' : 'hidden'};
  transition: all 0.2s ease;

  &:hover {
    ${props => !props.disabled && `
      transform: scale(1.02);
    `}
  }
`;

const ProfileImage = styled.img<{ size: number; $isError?: boolean }>`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 50%;
  overflow: hidden;
  ${props => props.$isError && `
    background-color: #f8f9fa;
    border: 2px solid #e9ecef;
    padding: 8px;
    box-sizing: border-box;
  `}
`;

const EmptyImageContainer = styled.div<{ size: number }>`
  width: 100%;
  height: 100%;
  background-color: #e5e5e5;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  border: 2px dashed #bdbdbd;
  transition: all 0.2s ease;

  &:hover {
    background-color: #f0f0f0;
    border-color: #999999;
  }
`;

const PlusIconWrapper = styled.div<{ size: number }>`
  width: ${props => props.size}px;
  height: ${props => props.size}px;
  color: #757575;
  display: flex;
  align-items: center;
  justify-content: center;
  
  svg {
    width: 100%;
    height: 100%;
  }
`;

const CameraIconWrapper = styled.div<{ size: number }>`
  position: absolute;
  bottom: ${props => props.size * 0.05}px;
  right: ${props => props.size * 0.05}px;
  width: ${props => props.size}px;
  height: ${props => props.size}px;
  color: #666666;
  background-color: white;
  border-radius: 50%;
  padding: ${props => props.size * 0.2}px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  border: 1px solid #e0e0e0;
  display: flex;
  align-items: center;
  justify-content: center;
  
  svg {
    width: ${props => props.size * 0.6}px;
    height: ${props => props.size * 0.6}px;
  }
`;

const DeleteIconWrapper = styled.div<{ size: number }>`
  position: absolute;
  top: ${props => props.size * 0.05}px;
  right: ${props => props.size * 0.05}px;
  width: ${props => props.size}px;
  height: ${props => props.size}px;
  color: #e53e3e;
  background-color: white;
  border-radius: 50%;
  padding: ${props => props.size * 0.2}px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  border: 1px solid #e0e0e0;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background-color: #fee;
    border-color: #e53e3e;
  }
  
  svg {
    width: ${props => props.size * 0.6}px;
    height: ${props => props.size * 0.6}px;
  }
`;

const HiddenInput = styled.input`
  display: none;
`;

export default ProfileImageAdd;
